<script setup lang="ts">
/* A destructive-action confirmation built on <Modal>.

   It owns the `busy` state and the error line rather than the caller,
   because a delete that fails has to be reported *in the dialog* — the
   row is still there, and closing on failure would look like success.
   `companies` deletes are the live example: profiles.company_id is
   `on delete restrict`, so deleting a company with members returns
   23503 and nothing is removed. */
import { ref, watch } from 'vue'
import Modal from './Modal.vue'
import Button from './Button.vue'
import Alert from './Alert.vue'

const props = defineProps<{
  open: boolean
  title: string
  /** The sentence that says what is about to happen, and to what. */
  message: string
  confirmLabel?: string
}>()

const emit = defineEmits<{ close: []; confirmed: [] }>()

const busy = ref(false)
const error = ref('')

watch(
  () => props.open,
  (open) => {
    if (open) {
      busy.value = false
      error.value = ''
    }
  },
)

/** Called by the parent when the delete throws. Keeps the dialog open. */
function fail(message: string): void {
  error.value = message
  busy.value = false
}

defineExpose({ fail })

function onConfirm(): void {
  busy.value = true
  error.value = ''
  emit('confirmed')
}
</script>

<template>
  <Modal
    :open="open"
    :title="title"
    :close-on-backdrop="!busy"
    @close="busy || emit('close')"
  >
    <p class="message">{{ message }}</p>
    <Alert v-if="error" tone="danger" class="error">{{ error }}</Alert>

    <template #footer>
      <Button variant="ghost" :disabled="busy" @click="emit('close')">Cancel</Button>
      <Button variant="danger" :loading="busy" @click="onConfirm">
        {{ confirmLabel ?? 'Delete' }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.message {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
  line-height: var(--leading-normal);
}

.error {
  margin-top: var(--space-sm);
}
</style>
